"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FileSearch, Search, Pencil as Edit, Wrench, File, MapPin, Link as LinkIcon, Tag, FileText, ChartBar as BarChart, ChartBarBig as BarChart2, Calendar } from 'lucide-react';
export default function Visibility() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "SEO website audit",
    description: "A comprehensive analysis of your site's current performance and health.",
    Icon: FileSearch,
    cluster: "Technical SEO"
  }, {
    number: "02",
    title: "Keyword research",
    description: "Identifying the high-value terms your customers are actually searching for.",
    Icon: Search,
    cluster: "Technical SEO"
  }, {
    number: "03",
    title: "On-page SEO optimization",
    description: "Fine-tuning your content and structure for maximum search visibility.",
    Icon: Edit,
    cluster: "Technical SEO"
  }, {
    number: "04",
    title: "Technical SEO improvements",
    description: "Fixing backend issues that prevent search engines from indexing you.",
    Icon: Wrench,
    cluster: "Technical SEO"
  }, {
    number: "05",
    title: "Service page optimization",
    description: "Turning your service pages into high-converting landing spots.",
    Icon: File,
    cluster: "Content & Keywords"
  }, {
    number: "06",
    title: "Local SEO support",
    description: "Dominating local search results to drive foot traffic and calls.",
    Icon: MapPin,
    cluster: "Content & Keywords"
  }, {
    number: "07",
    title: "Internal linking strategy",
    description: "Building a logical site structure that boosts authority across pages.",
    Icon: LinkIcon,
    cluster: "Content & Keywords"
  }, {
    number: "08",
    title: "Meta title and description updates",
    description: "Crafting compelling snippets that improve your click-through rates.",
    Icon: Tag,
    cluster: "Content & Keywords"
  }, {
    number: "09",
    title: "Content recommendations",
    description: "Strategic advice on what to write to attract and engage your audience.",
    Icon: FileText,
    cluster: "Analytics & Reporting"
  }, {
    number: "10",
    title: "Google Analytics Setup",
    description: "Implementing professional tracking to measure every visitor interaction.",
    Icon: BarChart,
    cluster: "Analytics & Reporting"
  }, {
    number: "11",
    title: "Google Search Console Setup",
    description: "Monitoring your site's presence in Google Search results directly.",
    Icon: BarChart2,
    cluster: "Analytics & Reporting"
  }, {
    number: "12",
    title: "Monthly SEO tracking and reporting",
    description: "Transparent data showing exactly how your visibility is growing.",
    Icon: Calendar,
    cluster: "Analytics & Reporting"
  }];
  return <section data-section-id="4553" id="visibilitypainpoints" className="bg-background text-foreground py-20 md:py-32 px-4 sm:px-6 lg:px-10 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-24 md:mb-32 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs tracking-widest uppercase text-primary mb-6">
              § 03 / Our Services
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tighter text-foreground">Is Your Website Showing Up When Customers Search?</h2>
          </div>
          <div className="flex !justify-center !items-center lg:col-span-7 lg:pl-8">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">A beautiful website is not enough if customers cannot find it. Many small businesses lose leads because their website is buried below competitors, missing important keywords, or not optimized for search. At Asahi Search, we bridge the gap between your business and your local customers. We ensure your brand is found, trusted, and chosen by the people who matter most.</p>
          </div>
        </div>

        <div className="space-y-24">
          {[{
          title: "Technical SEO",
          start: 0,
          end: 4
        }, {
          title: "Content & Keywords",
          start: 4,
          end: 8
        }, {
          title: "Analytics & Reporting",
          start: 8,
          end: 12
        }].map((group, groupIndex) => <div key={group.title} className="space-y-12">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-border/60" />
                <h3 className="font-mono text-xs tracking-widest uppercase text-primary/70 px-4 py-1 bg-secondary/30 rounded-lg">
                  {group.title}
                </h3>
                <div className="h-px flex-1 bg-border/60" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {capabilities.slice(group.start, group.end).map((cap, i) => {
              const Icon = cap.Icon;
              const isEven = i % 2 === 0;
              return <div data-index={i} key={cap.number} className={`group relative bg-background border border-border/60 hover:border-primary/60 transition-all duration-500 ease-out p-8 flex flex-col rounded-lg ${isEven ? 'shadow-md lg:translate-y-6' : 'shadow-lg lg:-translate-y-6'} hover:-translate-y-8 hover:shadow-xl`} style={{
                aspectRatio: "1 / 1.1",
                minHeight: "320px"
              }}>
                      {Icon && <div className="relative flex items-center mb-6 !justify-center">
                          <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-secondary/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <Icon strokeWidth={1.5} className="relative size-8 text-foreground/90 group-hover:text-primary transition-colors duration-500" />
                        </div>}

                      <h4 className="font-default font-semibold text-xl leading-tight text-foreground mb-4 !text-left">
                        {cap.title}
                      </h4>

                      <p className="font-default leading-relaxed text-muted-foreground/80 !text-lg">
                        {cap.description}
                      </p>

                      <div className="mt-auto pt-4">
                        <div className="w-8 h-1 bg-primary/20 group-hover:w-full group-hover:bg-primary/40 transition-all duration-500 rounded-lg" />
                      </div>
                    </div>;
            })}
              </div>
            </div>)}
        </div>

        <div className="mt-32 md:mt-40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 pt-12 border-t border-border">
          <div className="max-w-xl">
            <p className="font-mono uppercase tracking-widest text-primary mb-2 !text-base">Next Steps</p>
            <p className="font-default text-muted-foreground/90 !text-xl">Ready to grow your business? Let's start your journey with Asahi Search today and put your brand high on the search.</p>
          </div>
          <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-10 has-[>svg]:px-4 bg-primary text-primary-foreground font-default tracking-widest uppercase rounded-lg px-10 py-6 shadow-lg transition-all !text-xl disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-primary/20">
            <Link to="/ContactUsPage"><span className="!text-lg">Get Started</span></Link>
          </Button>
        </div>
      </div>
    </section>;
}